/**
 * useDashboardLayout — which main-dashboard layout to render.
 *
 * Two layouts coexist on /dashboard (Sabeeh, 2026-09-03 — see
 * DECISIONS_LOG "Dashboard v2 layout"):
 *   - 'v2'      — the redesigned page (outcomes bar, lane funnel with goal
 *                 boxes, reply handling, trend chart, attention panel,
 *                 campaigns table, latest positive replies)
 *   - 'classic' — the page exactly as it shipped before v2 (RetainerHero,
 *                 9-tile funnel, ratio row, POC pacing, activity row, assets)
 *
 * Rules:
 *   - Admins (owner / admin / developer) can pick either from the navbar
 *     profile menu; the choice is stored per browser in localStorage.
 *   - Client-role users never get a choice: they always see
 *     DEFAULT_DASHBOARD_LAYOUT, so every client sees the same surface.
 *   - DEFAULT_DASHBOARD_LAYOUT is the one-line rollback: flip it and every
 *     client plus every admin without a stored choice moves at once.
 *
 * Persistence + same-tab sync mirror useFunnelSettings.ts (localStorage +
 * a window CustomEvent) so the navbar toggle re-renders the page without
 * a reload. No DB involvement.
 */
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'

export type DashboardLayout = 'v2' | 'classic'

/** Flip to 'classic' to roll everyone back without touching anything else
 *  (clients always follow this value; admins keep their stored choice).
 *  Set to 'v2' on 2026-09-03 once all four v2 slices had shipped; back to
 *  'classic' the same day at Jamie's request, pending his sign-off on the
 *  design canvases. Admins can still preview v2 from the profile menu. */
export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = 'classic'

export const LS_DASHBOARD_LAYOUT_KEY = 'newux_dashboard_layout'
const LAYOUTS: DashboardLayout[] = ['v2', 'classic']
const SYNC_EVENT = 'newux:dashboard-layout-changed'

function read(): DashboardLayout {
  if (typeof window === 'undefined') return DEFAULT_DASHBOARD_LAYOUT
  try {
    const raw = window.localStorage.getItem(LS_DASHBOARD_LAYOUT_KEY)
    if (raw && (LAYOUTS as string[]).includes(raw)) return raw as DashboardLayout
  } catch {
    // localStorage disabled (Safari private mode, etc.) — fall through
  }
  return DEFAULT_DASHBOARD_LAYOUT
}

function write(value: DashboardLayout): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LS_DASHBOARD_LAYOUT_KEY, value)
    window.dispatchEvent(
      new CustomEvent(SYNC_EVENT, { detail: { key: LS_DASHBOARD_LAYOUT_KEY, value } }),
    )
  } catch {
    // ignore — losing persistence is non-fatal
  }
}

export interface DashboardLayoutControl {
  /** The layout to render for THIS user (client role → always the default). */
  layout: DashboardLayout
  /** The admin's stored choice, for the toggle UI. */
  storedLayout: DashboardLayout
  /** No-op for non-admins. */
  setLayout: (next: DashboardLayout) => void
  /** True only for admin-level roles. */
  canToggle: boolean
}

export function useDashboardLayout(): DashboardLayoutControl {
  const { isAdmin } = useAuth()
  const [stored, setStored] = useState<DashboardLayout>(() => read())

  useEffect(() => {
    const onSync = (ev: Event) => {
      const detail = (ev as CustomEvent<{ key: string; value: string }>).detail
      if (!detail || detail.key !== LS_DASHBOARD_LAYOUT_KEY) return
      if ((LAYOUTS as string[]).includes(detail.value)) setStored(detail.value as DashboardLayout)
    }
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== LS_DASHBOARD_LAYOUT_KEY || !ev.newValue) return
      if ((LAYOUTS as string[]).includes(ev.newValue)) setStored(ev.newValue as DashboardLayout)
    }
    window.addEventListener(SYNC_EVENT, onSync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(SYNC_EVENT, onSync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const setLayout = useCallback(
    (next: DashboardLayout) => {
      if (!isAdmin) return
      setStored(next)
      write(next)
    },
    [isAdmin],
  )

  return {
    layout: isAdmin ? stored : DEFAULT_DASHBOARD_LAYOUT,
    storedLayout: stored,
    setLayout,
    canToggle: isAdmin,
  }
}
